import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ImageOptionalLink } from '../../../commonTypes/links';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import { ContentOverlay } from '../Header/styles';
import {
  PrimaryNav,
  SecondaryNav,
  TertiaryNavItem,
  TertiaryNavItemWithIcon,
} from '../types';
import { LanguageToggleProps } from './LanguageToggle';
import Megamenu from './Megamenu';
import TopMenu from './TopMenu';
import { HeaderOuter, MegamenuPlaceholder } from './styles';
import useScrollCallback from './useScrollCallback';

const SCROLL_UPDATE_RATE_MS = 50;

export type DoubleHeaderProps = {
  defaultLogoLink: ImageOptionalLink;
  desktopLogoLink: ImageOptionalLink;
  phoneIconLink?: ImageOptionalLink;
  primaryNav?: PrimaryNav;
  secondaryNav?: SecondaryNav;
  /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
  tertiaryNavItem?: TertiaryNavItem | TertiaryNavItemWithIcon;
  /** @deprecated use tertiaryNavItems instead - if both are used, this will be ignored */
  secondTertiaryNavItem?: TertiaryNavItem;
  tertiaryNavItems?: (TertiaryNavItem | TertiaryNavItemWithIcon)[];
  languageToggle?: LanguageToggleProps;
  toggleMenuCallback?: (toggled: boolean) => void;
  mobileMenuOnOpen?: MouseEventHandler;
  mobileMenuOnClose?: MouseEventHandler;
  isSticky?: boolean;
  mobileHeaderText?: string;
  fontColourVariant?: boolean;
  forceHamburgerMenuIcon?: boolean;
  showTertiaryIconsMobile?: boolean;
  usePrimaryNavFourColumnLayout?: boolean;
  showArrowsOnPrimaryNav?: boolean;
  showMobilePhoneIcon?: boolean;
  enableKeyboardNavigation?: boolean;
} & ComponentProps;

const DoubleHeader: React.FC<DoubleHeaderProps> = ({
  defaultLogoLink,
  desktopLogoLink,
  phoneIconLink,
  primaryNav,
  secondaryNav,
  tertiaryNavItem,
  secondTertiaryNavItem,
  tertiaryNavItems,
  languageToggle,
  toggleMenuCallback,
  mobileMenuOnOpen,
  mobileMenuOnClose,
  isSticky,
  mobileHeaderText,
  fontColourVariant,
  enableKeyboardNavigation,
  forceHamburgerMenuIcon = false,
  showTertiaryIconsMobile = false,
  usePrimaryNavFourColumnLayout = false,
  showArrowsOnPrimaryNav = false,
  ...props
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeNavItemIndex, setActiveNavItemIndex] = useState<number | null>(null);

  useEffect(() => {
    toggleMenuCallback?.(activeNavItemIndex !== null);
  }, [activeNavItemIndex, toggleMenuCallback]);

  const resetNav = (): void => {
    setActiveNavItemIndex(null);
    setMobileMenuOpen(false);
  };

  const divRef = useRef<HTMLDivElement>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const updateScrollCallback = useCallback(() => {
    if (!divRef.current) {
      return;
    }
    const boundingRectangle = divRef.current.getBoundingClientRect();

    setShowStickyHeader(boundingRectangle.bottom < 0);
  }, [divRef]);
  useScrollCallback(updateScrollCallback, SCROLL_UPDATE_RATE_MS);

  const numberOfPrimaryNavItems = primaryNav?.navItems.length ?? 0;

  return (
    <>
      <HeaderOuter {...componentProps(props)} aria-label="main-header">
        <div ref={divRef}>
          {/* Secondary and Tertiary nav items internally aren't indexed including primary nav items, but externally are */}
          <TopMenu
            logoLink={desktopLogoLink}
            secondaryNav={secondaryNav}
            tertiaryNavItem={tertiaryNavItem}
            secondTertiaryNavItem={secondTertiaryNavItem}
            tertiaryNavItems={tertiaryNavItems}
            languageToggle={languageToggle}
            activeItemIndex={
              activeNavItemIndex === null || activeNavItemIndex < numberOfPrimaryNavItems
                ? null
                : activeNavItemIndex - numberOfPrimaryNavItems
            }
            setActiveItemIndex={(index: number | null) =>
              setActiveNavItemIndex(
                index === null ? null : numberOfPrimaryNavItems + index
              )
            }
            resetNav={resetNav}
          />
        </div>
        <MegamenuPlaceholder isSticky={isSticky && showStickyHeader} />
        <Megamenu
          primaryNav={primaryNav}
          secondaryNav={secondaryNav}
          tertiaryNavItem={tertiaryNavItem}
          secondTertiaryNavItem={secondTertiaryNavItem}
          tertiaryNavItems={tertiaryNavItems}
          languageToggle={languageToggle}
          toggleMenuCallback={toggleMenuCallback}
          mobileMenuOpen={mobileMenuOpen}
          toggleMobileMenuOpen={() => setMobileMenuOpen((state) => !state)}
          mobileMenuOnOpen={mobileMenuOnOpen}
          mobileMenuOnClose={mobileMenuOnClose}
          logoLink={defaultLogoLink}
          activeNavItemIndex={activeNavItemIndex}
          setActiveNavItemIndex={setActiveNavItemIndex}
          resetNav={resetNav}
          isSticky={isSticky && showStickyHeader}
          mobileHeaderText={mobileHeaderText}
          fontColourVariant={fontColourVariant}
          forceHamburgerMenuIcon={forceHamburgerMenuIcon}
          showTertiaryIconsMobile={showTertiaryIconsMobile}
          usePrimaryNavFourColumnLayout={usePrimaryNavFourColumnLayout}
          showArrowsOnPrimaryNav={showArrowsOnPrimaryNav}
          phoneIconLink={phoneIconLink}
          enableKeyboardNavigation={enableKeyboardNavigation}
        />
      </HeaderOuter>
      <ContentOverlay
        activeMobile={mobileMenuOpen}
        activeDesktop={activeNavItemIndex !== null}
        onClick={resetNav}
        aria-hidden={!(mobileMenuOpen || activeNavItemIndex !== null)}
      />
    </>
  );
};

export default applyComponentTheme(DoubleHeader, 'DoubleHeader');
